import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Target } from "lucide-react";
import coverPlaybook from "@/assets/cover-playbook-de-vendas.jpg";

const PlaybookDeVendas = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Playbook de Vendas: Como Criar o Manual do Vendedor Que o Time Realmente Usa",
    description:
      "Como criar playbook de vendas na prática: estrutura do manual do vendedor, o que entra em cada capítulo, como implantar o playbook comercial e como mantê-lo vivo.",
    image: "https://bianchimbusiness.com.br/cover-playbook-de-vendas.jpg",
    datePublished: "2026-09-04",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/playbook-de-vendas",
    },
  };

  const capitulos = [
    {
      capitulo: "1. Quem somos e o que vendemos",
      conteudo: "Proposta de valor em uma frase, o problema que resolvemos e o que NÃO fazemos",
      erroComum: "Copiar o texto institucional do site em vez de escrever como o vendedor fala",
    },
    {
      capitulo: "2. Cliente ideal e anti-cliente",
      conteudo: "ICP por porte, setor, região, gatilhos de compra e os perfis que devem ser recusados",
      erroComum: "Definir o ICP amplo demais para não perder oportunidade — e perder foco",
    },
    {
      capitulo: "3. Etapas do funil e critérios de passagem",
      conteudo: "O que precisa ser verdade para uma oportunidade avançar de etapa",
      erroComum: "Etapas nomeadas por ação do vendedor e não por decisão do cliente",
    },
    {
      capitulo: "4. Abordagem e descoberta",
      conteudo: "Aberturas por canal, perguntas de diagnóstico e o que registrar no CRM",
      erroComum: "Roteiro decorado palavra por palavra em vez de blocos com intenção clara",
    },
    {
      capitulo: "5. Proposta e negociação",
      conteudo: "Faixas de desconto, alçadas de aprovação, condições e política de concessão",
      erroComum: "Deixar desconto no critério pessoal do vendedor",
    },
    {
      capitulo: "6. Objeções e respostas de referência",
      conteudo: "As 10 objeções mais frequentes com abordagens que já funcionaram na sua operação",
      erroComum: "Respostas prontas genéricas, tiradas de livro, que ninguém usa no telefone",
    },
    {
      capitulo: "7. Fechamento e pós-venda",
      conteudo: "Como conduzir a decisão, documentos, passagem para implantação e pedido de indicação",
      erroComum: "O playbook acabar na assinatura e ignorar a expansão da carteira",
    },
    {
      capitulo: "8. Rotina, metas e indicadores",
      conteudo: "Agenda semanal, número mínimo de atividades e KPIs de acompanhamento",
      erroComum: "Metas sem rotina: a meta é cobrada, mas ninguém combinou o caminho",
    },
  ];

  const sinais = [
    {
      sinal: "Cada vendedor vende de um jeito",
      custo: "Resultado depende de talento individual; contratar não escala",
    },
    {
      sinal: "Vendedor novo demora meses para produzir",
      custo: "Ramp-up longo, folha pesando antes da receita chegar",
    },
    {
      sinal: "O gestor responde as mesmas dúvidas toda semana",
      custo: "Liderança virou suporte operacional em vez de desenvolver o time",
    },
    {
      sinal: "Descontos variam sem critério",
      custo: "Margem corroída e cliente comparando condições entre si",
    },
    {
      sinal: "Quando um bom vendedor sai, o conhecimento vai junto",
      custo: "A empresa recomeça do zero a cada troca de time",
    },
  ];

  return (
    <>
      <SEOHead
        title="Playbook de Vendas: Como Criar o Manual do Vendedor (2026) | Bianchim Business"
        description="Como criar playbook de vendas passo a passo: estrutura do manual do vendedor, capítulos essenciais, implantação do playbook comercial e como mantê-lo vivo."
        keywords="playbook de vendas, como criar playbook de vendas, manual do vendedor, playbook comercial, processo comercial, padronização de vendas, playbook de vendas B2B"
        canonicalUrl="https://bianchimbusiness.com.br/posts/playbook-de-vendas"
        ogType="article"
        articlePublishedTime="2026-09-04"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverPlaybook}
                  alt="Playbook de vendas aberto sobre a mesa com diagramas do processo comercial"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Processo Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Playbook de vendas:{" "}
                    <span className="text-gradient-brand">
                      como criar o manual do vendedor que o time realmente usa
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>12 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>04 Set 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Existe um documento que quase toda empresa diz ter e quase nenhuma consegue
                  mostrar quando você pede: o playbook de vendas. Na maioria das operações ele é um
                  PDF de 60 páginas feito por uma consultoria, salvo em uma pasta que ninguém abre
                  desde a semana em que foi entregue. E o time continua vendendo cada um do seu
                  jeito.
                </p>
                <p>
                  Playbook comercial não é um manual bonito. É a memória da sua empresa sobre o que
                  funciona na venda — escrita de um jeito que o vendedor consegue usar entre uma
                  ligação e outra. Aqui eu mostro como criar playbook de vendas do zero, o que entra
                  em cada capítulo, como implantar sem virar burocracia e como manter esse manual do
                  vendedor vivo depois do primeiro mês.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. Como saber se a sua empresa precisa de um playbook agora
                </h2>
                <p>
                  Nem toda empresa precisa do documento no dia em que abre. Mas a partir do segundo
                  ou terceiro vendedor, a falta dele começa a cobrar caro — e o custo aparece sempre
                  nos mesmos sintomas.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Sinal na operação</th>
                        <th className="p-3 font-semibold">O que isso custa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sinais.map((s) => (
                        <tr key={s.sinal} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{s.sinal}</td>
                          <td className="p-3">{s.custo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  Se você marcou dois ou mais desses itens, o problema não é motivação do time: é
                  ausência de método documentado. E método documentado é exatamente o que um
                  playbook de vendas entrega.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. A estrutura do manual do vendedor: oito capítulos que bastam
                </h2>
                <p>
                  Um bom playbook cabe em 20 a 30 páginas. O que faz diferença não é o tamanho, é a
                  especificidade: cada capítulo deve descrever a sua venda, com o seu produto, o seu
                  cliente e as frases que o seu time usa. Abaixo, a estrutura que eu uso em
                  implantação com clientes — e o erro que mais aparece em cada parte.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Capítulo</th>
                        <th className="p-3 font-semibold">O que entra</th>
                        <th className="p-3 font-semibold">Erro comum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capitulos.map((c) => (
                        <tr key={c.capitulo} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{c.capitulo}</td>
                          <td className="p-3">{c.conteudo}</td>
                          <td className="p-3">{c.erroComum}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  Dois capítulos merecem atenção redobrada. O de abordagem, porque é onde o playbook
                  encosta no dia a dia — use como base um{" "}
                  <Link to="/posts/script-de-vendas-pronto" className="text-primary underline">
                    script de vendas pronto
                  </Link>{" "}
                  e adapte com a linguagem da sua operação. E o de indicadores, que precisa estar
                  amarrado às{" "}
                  <Link to="/posts/metricas-e-kpis-de-vendas" className="text-primary underline">
                    métricas e KPIs de vendas
                  </Link>{" "}
                  que a liderança já acompanha, senão vira um capítulo decorativo.
                </p>

                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                  <div className="flex items-center gap-2 mb-3 text-primary">
                    <Target className="w-5 h-5" />
                    <span className="font-semibold">Playbook lido não muda comportamento</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                    O manual só vira resultado quando o vendedor executa ele em voz alta
                  </h3>
                  <p className="mb-5">
                    Ler o capítulo de objeções não ensina ninguém a contornar objeção. No Vendas
                    Trainer, o vendedor simula a conversa com personas de IA no perfil dos seus
                    clientes — o cético, o que só fala de preço, o que enrola a decisão — e recebe
                    nota de 0 a 100 com feedback de abertura, descoberta, contorno e fechamento. É
                    assim que o playbook sai do papel e entra na boca do time em dias, não em meses.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis e treinar o playbook
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/calculadora-roi")}>
                      Calcular o ROI do treinamento
                    </Button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. Como escrever: comece pelo que já dá certo, não pela teoria
                </h2>
                <p>
                  O maior erro de quem monta o primeiro playbook é escrever a venda ideal em vez da
                  venda real. O caminho mais rápido é o inverso: pegue os cinco últimos contratos
                  fechados e os cinco últimos perdidos e reconstrua a história de cada um. Por onde
                  o cliente chegou, qual foi a primeira frase que prendeu a atenção, que pergunta
                  mudou a conversa, qual objeção apareceu, o que destravou a decisão. O padrão que
                  se repete nos ganhos é o seu método — e ele já existe, só não estava escrito.
                </p>
                <p>
                  Depois disso, envolva o time na redação. Um playbook escrito só pela diretoria é
                  recebido como imposição; um playbook em que o vendedor sênior reconhece as
                  próprias frases é adotado por orgulho. Na prática: duas horas de reunião com os
                  melhores vendedores, gravadas, gerando o rascunho dos capítulos de abordagem,
                  objeções e fechamento.
                </p>
                <p>
                  Escreva em linguagem falada. Nada de "realizar o levantamento das necessidades do
                  prospect". Escreva "pergunte quanto tempo um vendedor novo leva para bater meta aí
                  hoje". O teste é simples: se a frase não pode ser dita numa ligação, ela não entra
                  no manual do vendedor.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. Implantação: as quatro primeiras semanas decidem tudo
                </h2>
                <p>
                  Entregar o arquivo e pedir para o time ler é a forma mais confiável de matar um
                  playbook comercial. A implantação que funciona é curta e barulhenta: uma reunião
                  de apresentação em que a liderança explica o porquê, não o conteúdo; depois, quatro
                  semanas em que cada reunião semanal aprofunda um bloco — semana 1 abordagem,
                  semana 2 descoberta, semana 3 objeções, semana 4 fechamento — sempre com simulação
                  ao vivo, nunca com leitura em grupo.
                </p>
                <p>
                  A liderança precisa usar o vocabulário do playbook nas revisões de pipeline. Se o
                  gestor pergunta "e aí, como tá esse cliente?" em vez de "ele atende os critérios
                  de passagem da etapa 3?", o documento morre em três semanas. O playbook vira
                  cultura quando ele é a linguagem da cobrança, e não um anexo dela. Para times em
                  formação, esse ritual se conecta direto com o processo de{" "}
                  <Link to="/posts/como-treinar-vendedores-novos" className="text-primary underline">
                    treinar vendedores novos
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. Como manter o playbook vivo (e não virar peça de museu)
                </h2>
                <p>
                  Mercado muda, concorrente muda, objeção nova aparece. Um playbook que não é
                  revisado envelhece em um trimestre. Estabeleça um dono — normalmente o líder
                  comercial — e uma revisão trimestral de uma hora com três perguntas: que objeção
                  nova apareceu, que argumento parou de funcionar e que caso de sucesso merece virar
                  referência no documento.
                </p>
                <p>
                  Guarde o playbook em um lugar editável e pesquisável, não em PDF. Marque a data da
                  última revisão em cada capítulo. E registre os áudios ou trechos de reuniões reais
                  que exemplificam o que está escrito: um vendedor aprende dez vezes mais ouvindo um
                  colega contornar uma objeção do que lendo o parágrafo que descreve a técnica. Se a
                  sua operação faz prospecção ativa, mantenha o capítulo de abordagem alinhado com o
                  que descrevi no guia de{" "}
                  <Link to="/posts/prospeccao-b2b" className="text-primary underline">
                    prospecção B2B
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: o playbook é o ativo que fica quando o vendedor vai embora
                </h2>
                <p>
                  Empresa sem playbook de vendas depende de pessoas; empresa com playbook depende de
                  método. A diferença aparece na hora de contratar, de escalar, de trocar alguém do
                  time ou de vender a própria operação. Comece pequeno: escreva os capítulos de
                  cliente ideal, abordagem e objeções nesta semana, teste com o time por 30 dias,
                  ajuste e só então complete o restante. Um playbook de oito páginas em uso vale
                  muito mais que sessenta páginas paradas. E se o time ainda está sendo estruturado,
                  vale ler antes como{" "}
                  <Link to="/posts/como-montar-equipe-de-vendas-do-zero" className="text-primary underline">
                    montar uma equipe de vendas do zero
                  </Link>
                  .
                </p>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PlaybookDeVendas;
